// Holographic Square Sticker Customizer - Core JavaScript
// Background: animated holographic shimmer (preview only)
// Export: transparent PNG — holographic material is the background

(function() {
  'use strict';

  const state = {
    uploadedImage: null,
    uploadedImageUrl: null,
    cloudinaryUrl: null,
    canvas: null,
    ctx: null,
    canvasSize: 450,
    imagePosition: { x: 225, y: 225 },
    imageScale: 1,
    imageRotation: 0,
    brightness: 100,
    contrast: 100,
    saturation: 100,
    flipHorizontal: false,
    flipVertical: false,
    textElements: [],
    shapeElements: [],
    selectedTextColor: '#000000',
    selectedFont: 'Roboto',
    fontSize: 32,
    letterSpacing: 0,
    lineHeight: 1.2,
    textOutline: false,
    textGradient: false,
    isDragging: false,
    isResizing: false,
    selectedElement: null,
    dragStart: { x: 0, y: 0 },
    dragOffset: { x: 0, y: 0 },
    resizeHandle: null,
    initialSize: 0,
    initialDistance: 0,
    currentTextInput: '',
    imageLayer: 1,
    productData: {
      quantity: '',
      size: '',
      price: '',
      title: '',
      variantId: ''
    },
    exportCanvas: null,
    exportCtx: null,
    holoHue: 0,
    holoShine: 0,
    animationFrame: null
  };

  document.addEventListener('DOMContentLoaded', function() {
    initializeCustomizer();
  });

  function initializeCustomizer() {
    state.canvas = document.getElementById('designCanvas');
    if (!state.canvas) { console.error('Canvas not found'); return; }
    state.ctx = state.canvas.getContext('2d');

    state.exportCanvas = document.createElement('canvas');
    state.exportCanvas.width = 1800;
    state.exportCanvas.height = 1800;
    state.exportCtx = state.exportCanvas.getContext('2d');

    loadProductData();
    alignSliders();
    setupTabNavigation();
    setupUpload();
    setupImageControls();
    setupTextControls();
    setupElementControls();
    setupSaveButton();
    setupResetButton();
    setupCanvasInteraction();
    setupKeyboardControls();

    startHoloAnimation();
  }

  // ============================================
  // HOLOGRAPHIC ANIMATION
  // ============================================

  function startHoloAnimation() {
    function animate() {
      state.holoHue = (state.holoHue + 0.5) % 360;
      state.holoShine = (state.holoShine + 1.2) % 500;
      renderCanvas();
      state.animationFrame = requestAnimationFrame(animate);
    }
    state.animationFrame = requestAnimationFrame(animate);
  }

  function drawHolographicBackground(ctx) {
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(0, 0, 450, 450, 20);
    ctx.clip();

    // Silver-white base
    ctx.fillStyle = 'rgb(220, 220, 225)';
    ctx.fillRect(0, 0, 450, 450);

    // Soft pastel color shift on top of silver base
    const grad = ctx.createLinearGradient(0, 0, 450, 450);
    const hue = state.holoHue;
    grad.addColorStop(0,    `hsla(${hue}, 50%, 78%, 0.55)`);
    grad.addColorStop(0.25, `hsla(${(hue + 60)  % 360}, 45%, 80%, 0.55)`);
    grad.addColorStop(0.5,  `hsla(${(hue + 150) % 360}, 48%, 82%, 0.55)`);
    grad.addColorStop(0.75, `hsla(${(hue + 220) % 360}, 45%, 80%, 0.55)`);
    grad.addColorStop(1,    `hsla(${hue}, 50%, 78%, 0.55)`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 450, 450);

    // Subtle moving light streak
    const shineX = state.holoShine - 50;
    const shineGrad = ctx.createLinearGradient(shineX - 60, 0, shineX + 60, 450);
    shineGrad.addColorStop(0,    'rgba(255,255,255,0)');
    shineGrad.addColorStop(0.45, 'rgba(255,255,255,0)');
    shineGrad.addColorStop(0.5,  'rgba(255,255,255,0.18)');
    shineGrad.addColorStop(0.55, 'rgba(255,255,255,0)');
    shineGrad.addColorStop(1,    'rgba(255,255,255,0)');
    ctx.fillStyle = shineGrad;
    ctx.fillRect(0, 0, 450, 450);

    ctx.restore();
  }

  // ============================================
  // PRODUCT DATA
  // ============================================

  function loadProductData() {
    const urlParams = new URLSearchParams(window.location.search);
    state.productData.quantity  = urlParams.get('quantity') || '';
    state.productData.size      = urlParams.get('size')     || '';
    state.productData.price     = urlParams.get('price')    || '';
    state.productData.title     = urlParams.get('title')    || 'Square Holographic Stickers';
    state.productData.variantId = urlParams.get('variant')  || '';

    if (!state.productData.variantId) tryToFindVariant();

    const productInfo = document.getElementById('productInfo');
    if (productInfo && state.productData.quantity && state.productData.size && state.productData.price) {
      productInfo.textContent = `${state.productData.quantity} ${state.productData.title} • ${state.productData.size} • $${state.productData.price}`;
    } else if (productInfo) {
      productInfo.textContent = 'Square Holographic Sticker Customizer';
    }
  }

  function tryToFindVariant() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('select[name="id"], input[name="id"]');
      if (variantInput && variantInput.value) { state.productData.variantId = variantInput.value; return; }
    }
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return_url');
    if (returnUrl) {
      const match = returnUrl.match(/variant=(\d+)/);
      if (match && match[1]) { state.productData.variantId = match[1]; return; }
    }
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.selectedVariantId) {
      state.productData.variantId = window.ShopifyAnalytics.meta.selectedVariantId;
    }
  }

  // ============================================
  // SLIDERS
  // ============================================

  function alignSliders() {
    const fontSizeSlider      = document.getElementById('fontSizeSlider');
    const letterSpacingSlider = document.getElementById('letterSpacingSlider');
    const lineHeightSlider    = document.getElementById('lineHeightSlider');
    if (fontSizeSlider)      { fontSizeSlider.min = 12; fontSizeSlider.max = 112; fontSizeSlider.value = 32; }
    if (letterSpacingSlider) { letterSpacingSlider.min = -10; letterSpacingSlider.max = 40; letterSpacingSlider.value = 0; }
    if (lineHeightSlider)    { lineHeightSlider.min = 0.8; lineHeightSlider.max = 2.8; lineHeightSlider.step = 0.1; lineHeightSlider.value = 1.2; }
  }

  // ============================================
  // TAB NAVIGATION
  // ============================================

  function setupTabNavigation() {
    const sidebarIcons = document.querySelectorAll('.sidebar-icon');
    sidebarIcons.forEach(icon => {
      icon.addEventListener('click', function() {
        const tab = this.getAttribute('data-tab');
        sidebarIcons.forEach(i => i.classList.remove('active'));
        this.classList.add('active');
        document.querySelectorAll('.panel-content').forEach(p => p.classList.remove('active'));
        const targetPanel = document.querySelector(`[data-content="${tab}"]`);
        if (targetPanel) targetPanel.classList.add('active');
      });
    });
  }

  // ============================================
  // UPLOAD
  // ============================================

  function setupUpload() {
    const uploadArea   = document.getElementById('uploadArea');
    const fileInput    = document.getElementById('fileInput');
    const removeBtn    = document.getElementById('removeImageBtn');
    const uploadPrompt = document.getElementById('uploadPrompt');

    if (uploadArea && fileInput) {
      uploadArea.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', function(e) {
        if (e.target.files && e.target.files[0]) handleFileUpload(e.target.files[0]);
      });
      uploadArea.addEventListener('dragover',  function(e) { e.preventDefault(); this.classList.add('dragging'); });
      uploadArea.addEventListener('dragleave', function(e) { e.preventDefault(); this.classList.remove('dragging'); });
      uploadArea.addEventListener('drop', function(e) {
        e.preventDefault();
        this.classList.remove('dragging');
        if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0]);
      });
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', function() {
        state.uploadedImage = null;
        state.uploadedImageUrl = null;
        state.cloudinaryUrl = null;
        state.selectedElement = null;
        state.imageScale = 1;
        state.imageLayer = 1;
        state.imagePosition = { x: 225, y: 225 };
        state.imageRotation = 0;
        state.brightness = 100;
        state.contrast = 100;
        state.saturation = 100;
        state.flipHorizontal = false;
        state.flipVertical = false;
        document.getElementById('uploadedImageInfo').style.display = 'none';
        if (uploadPrompt) uploadPrompt.classList.remove('hidden');
        const fi = document.getElementById('fileInput');
        if (fi) fi.value = '';
        const zoomSlider = document.getElementById('zoomSlider');
        const zoomValue  = document.getElementById('zoomValue');
        if (zoomSlider) zoomSlider.value = 100;
        if (zoomValue)  zoomValue.textContent = '100%';
        document.getElementById('rotationSlider').value = 0;
        document.getElementById('rotationValue').textContent = '0°';
        document.getElementById('brightnessSlider').value = 100;
        document.getElementById('brightnessValue').textContent = '100%';
        document.getElementById('contrastSlider').value = 100;
        document.getElementById('contrastValue').textContent = '100%';
        document.getElementById('saturationSlider').value = 100;
        document.getElementById('saturationValue').textContent = '100%';
      });
    }
  }

  function handleFileUpload(file) {
    if (!file.type.match('image.*')) { showMessage('Please upload an image file', 'error'); return; }
    if (file.size > 50 * 1024 * 1024) { showMessage('File size must be under 50MB', 'error'); return; }
    showMessage('Uploading image...', 'info');
    uploadToCloudinary(file);
  }

  function uploadToCloudinary(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', window.cloudinaryConfig.uploadPreset);
    formData.append('folder', window.cloudinaryConfig.folder);

    fetch(`https://api.cloudinary.com/v1_1/${window.cloudinaryConfig.cloudName}/image/upload`, {
      method: 'POST',
      body: formData
    })
    .then(r => r.json())
    .then(data => {
      if (data.secure_url) {
        state.cloudinaryUrl = data.secure_url;
        loadImageToCanvas(data.secure_url);
        document.getElementById('uploadedImageInfo').style.display = 'block';
        document.getElementById('imageFileName').textContent = file.name;
        const uploadPrompt = document.getElementById('uploadPrompt');
        if (uploadPrompt) uploadPrompt.classList.add('hidden');
        showMessage('Image uploaded successfully!', 'success');
      } else {
        showMessage('Upload failed. Please try again.', 'error');
      }
    })
    .catch(() => showMessage('Upload failed. Please try again.', 'error'));
  }

  function loadImageToCanvas(url) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function() {
      state.uploadedImage = img;
      state.uploadedImageUrl = url;
      state.imagePosition = { x: 225, y: 225 };
      const squareSize = 450;
      const imgLongestSide = Math.max(img.width, img.height);
      state.imageScale = squareSize / imgLongestSide;
      const zoomSlider = document.getElementById('zoomSlider');
      const zoomValue  = document.getElementById('zoomValue');
      if (zoomSlider) zoomSlider.value = 100;
      if (zoomValue)  zoomValue.textContent = '100%';
      state.imageLayer = Math.max(state.textElements.length, state.shapeElements.length) + 1;
    };
    img.src = url;
  }

  // ============================================
  // IMAGE CONTROLS
  // ============================================

  function setupImageControls() {
    const zoomSlider = document.getElementById('zoomSlider');
    const zoomValue  = document.getElementById('zoomValue');
    if (zoomSlider && zoomValue) {
      zoomSlider.min = 10; zoomSlider.max = 2000; zoomSlider.value = 100;
      zoomSlider.addEventListener('input', function() {
        if (state.uploadedImage) {
          const imgLongestSide = Math.max(state.uploadedImage.width, state.uploadedImage.height);
          const baseFit = 450 / imgLongestSide;
          state.imageScale = baseFit * (this.value / 100);
        }
        zoomValue.textContent = this.value + '%';
      });
    }

    const rotationSlider = document.getElementById('rotationSlider');
    const rotationValue  = document.getElementById('rotationValue');
    if (rotationSlider && rotationValue) {
      rotationSlider.addEventListener('input', function() {
        state.imageRotation = this.value;
        rotationValue.textContent = this.value + '°';
      });
    }

    const brightnessSlider = document.getElementById('brightnessSlider');
    const brightnessValue  = document.getElementById('brightnessValue');
    if (brightnessSlider && brightnessValue) {
      brightnessSlider.addEventListener('input', function() {
        state.brightness = this.value;
        brightnessValue.textContent = this.value + '%';
      });
    }

    const contrastSlider = document.getElementById('contrastSlider');
    const contrastValue  = document.getElementById('contrastValue');
    if (contrastSlider && contrastValue) {
      contrastSlider.addEventListener('input', function() {
        state.contrast = this.value;
        contrastValue.textContent = this.value + '%';
      });
    }

    const saturationSlider = document.getElementById('saturationSlider');
    const saturationValue  = document.getElementById('saturationValue');
    if (saturationSlider && saturationValue) {
      saturationSlider.addEventListener('input', function() {
        state.saturation = this.value;
        saturationValue.textContent = this.value + '%';
      });
    }

    const flipHBtn = document.getElementById('flipHorizontalBtn');
    const flipVBtn = document.getElementById('flipVerticalBtn');
    if (flipHBtn) flipHBtn.addEventListener('click', function() { state.flipHorizontal = !state.flipHorizontal; });
    if (flipVBtn) flipVBtn.addEventListener('click', function() { state.flipVertical   = !state.flipVertical; });

    const imageFrontBtn = document.getElementById('imageFrontBtn');
    const imageBackBtn  = document.getElementById('imageBackBtn');
    if (imageFrontBtn) {
      imageFrontBtn.addEventListener('click', function() {
        if (!state.uploadedImage) { showMessage('Please upload an image first', 'info'); return; }
        state.imageLayer = getMaxLayer() + 1;
        showMessage('Image moved to front', 'success');
      });
    }
    if (imageBackBtn) {
      imageBackBtn.addEventListener('click', function() {
        if (!state.uploadedImage) { showMessage('Please upload an image first', 'info'); return; }
        state.imageLayer = 0;
        showMessage('Image moved to back', 'success');
      });
    }
  }

  function getMaxLayer() {
    return Math.max(
      state.imageLayer || 0,
      state.textElements.length  > 0 ? Math.max(...state.textElements.map(t  => t.layer  || 0)) : 0,
      state.shapeElements.length > 0 ? Math.max(...state.shapeElements.map(s => s.layer || 0)) : 0
    );
  }

  // ============================================
  // TEXT CONTROLS
  // ============================================

  function setupTextControls() {
    const textInput  = document.getElementById('textInput');
    const addTextBtn = document.getElementById('addTextBtn');
    if (addTextBtn) addTextBtn.style.display = 'none';

    if (textInput) {
      textInput.addEventListener('input', function() {
        state.currentTextInput = this.value;
        if (state.currentTextInput && state.textElements.length === 0) {
          state.textElements.push({
            text: state.currentTextInput,
            x: 225, y: 225,
            font: state.selectedFont,
            size: state.fontSize,
            color: state.selectedTextColor,
            letterSpacing: state.letterSpacing,
            lineHeight: state.lineHeight,
            outline: state.textOutline,
            gradient: state.textGradient,
            width: 100, height: 50,
            layer: 1000
          });
          state.selectedElement = { type: 'text', index: 0 };
        } else if (state.selectedElement && state.selectedElement.type === 'text') {
          state.textElements[state.selectedElement.index].text = state.currentTextInput;
        }
      });

      textInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          const pos = this.selectionStart;
          this.value = this.value.substring(0, pos) + '\n' + this.value.substring(pos);
          this.selectionStart = this.selectionEnd = pos + 1;
          this.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    }

    const fontSelected = document.getElementById('fontSelected');
    const fontOptions  = document.getElementById('fontOptions');
    if (fontSelected && fontOptions) {
      fontSelected.addEventListener('click', function() {
        fontOptions.classList.toggle('open');
        fontSelected.classList.toggle('open');
      });
      fontOptions.querySelectorAll('.font-option').forEach(option => {
        option.addEventListener('click', function() {
          fontOptions.querySelectorAll('.font-option').forEach(o => o.classList.remove('selected'));
          this.classList.add('selected');
          state.selectedFont = this.getAttribute('data-font');
          fontSelected.textContent = state.selectedFont;
          fontSelected.style.fontFamily = this.style.fontFamily;
          fontOptions.classList.remove('open');
          fontSelected.classList.remove('open');
          if (state.selectedElement && state.selectedElement.type === 'text') {
            state.textElements[state.selectedElement.index].font = state.selectedFont;
          }
        });
      });
      document.addEventListener('click', function(e) {
        if (!e.target.closest('.font-dropdown-custom')) {
          fontOptions.classList.remove('open');
          fontSelected.classList.remove('open');
        }
      });
    }

    setupSliderControl('fontSizeSlider',      'fontSizeValue',      'px', function(v) { state.fontSize = parseInt(v); if (state.selectedElement && state.selectedElement.type === 'text') state.textElements[state.selectedElement.index].size = state.fontSize; });
    setupSliderControl('letterSpacingSlider', 'letterSpacingValue', 'px', function(v) { state.letterSpacing = parseInt(v); if (state.selectedElement && state.selectedElement.type === 'text') state.textElements[state.selectedElement.index].letterSpacing = state.letterSpacing; });
    setupSliderControl('lineHeightSlider',    'lineHeightValue',    '',   function(v) { state.lineHeight = parseFloat(v); if (state.selectedElement && state.selectedElement.type === 'text') state.textElements[state.selectedElement.index].lineHeight = state.lineHeight; });

    const textColorGrid = document.getElementById('textColorGrid');
    if (textColorGrid) {
      textColorGrid.querySelectorAll('.color-swatch').forEach(swatch => {
        swatch.addEventListener('click', function() {
          textColorGrid.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('selected'));
          this.classList.add('selected');
          state.selectedTextColor = this.getAttribute('data-color');
          if (state.selectedElement && state.selectedElement.type === 'text') {
            state.textElements[state.selectedElement.index].color = state.selectedTextColor;
          }
        });
      });
    }

    const textOutlineBtn  = document.getElementById('textOutlineBtn');
    const textGradientBtn = document.getElementById('textGradientBtn');
    if (textOutlineBtn) {
      textOutlineBtn.addEventListener('click', function() {
        state.textOutline = !state.textOutline;
        this.style.opacity = state.textOutline ? '1' : '0.7';
        if (state.selectedElement && state.selectedElement.type === 'text') state.textElements[state.selectedElement.index].outline = state.textOutline;
      });
    }
    if (textGradientBtn) {
      textGradientBtn.addEventListener('click', function() {
        state.textGradient = !state.textGradient;
        this.style.opacity = state.textGradient ? '1' : '0.7';
        if (state.selectedElement && state.selectedElement.type === 'text') state.textElements[state.selectedElement.index].gradient = state.textGradient;
      });
    }

    const textFrontBtn = document.getElementById('textFrontBtn');
    const textBackBtn  = document.getElementById('textBackBtn');
    if (textFrontBtn) {
      textFrontBtn.addEventListener('click', function() {
        if (!state.selectedElement || state.selectedElement.type !== 'text') { showMessage('Please select a text element first', 'info'); return; }
        state.textElements[state.selectedElement.index].layer = getMaxLayer() + 1;
        showMessage('Text moved to front', 'success');
      });
    }
    if (textBackBtn) {
      textBackBtn.addEventListener('click', function() {
        if (!state.selectedElement || state.selectedElement.type !== 'text') { showMessage('Please select a text element first', 'info'); return; }
        state.textElements[state.selectedElement.index].layer = 0;
        showMessage('Text moved to back', 'success');
      });
    }
  }

  function setupSliderControl(sliderId, valueId, suffix, callback) {
    const slider  = document.getElementById(sliderId);
    const display = document.getElementById(valueId);
    if (slider && display) {
      slider.addEventListener('input', function() {
        display.textContent = this.value + suffix;
        callback(this.value);
      });
    }
  }

  // ============================================
  // ELEMENT CONTROLS
  // ============================================

  function setupElementControls() {
    document.querySelectorAll('.element-item').forEach(item => {
      item.addEventListener('click', function() {
        addElementToCanvas(this.getAttribute('data-element'));
      });
    });

    const bringToFrontBtn  = document.getElementById('bringToFrontBtn');
    const sendToBackBtn    = document.getElementById('sendToBackBtn');
    const clearElementsBtn = document.getElementById('clearElementsBtn');

    if (bringToFrontBtn) {
      bringToFrontBtn.addEventListener('click', function() {
        if (!state.selectedElement || state.selectedElement.type !== 'shape') { showMessage('Please select an element first', 'info'); return; }
        state.shapeElements[state.selectedElement.index].layer = getMaxLayer() + 1;
        showMessage('Element moved to front', 'success');
      });
    }
    if (sendToBackBtn) {
      sendToBackBtn.addEventListener('click', function() {
        if (!state.selectedElement || state.selectedElement.type !== 'shape') { showMessage('Please select an element first', 'info'); return; }
        state.shapeElements[state.selectedElement.index].layer = 0;
        showMessage('Element moved to back', 'success');
      });
    }
    if (clearElementsBtn) {
      clearElementsBtn.addEventListener('click', function() {
        state.shapeElements = [];
        state.selectedElement = null;
        showMessage('All elements cleared', 'success');
      });
    }
  }

  function addElementToCanvas(type) {
    state.shapeElements.push({ type, x: 225, y: 225, size: 50, color: '#000000', rotation: 0, layer: getMaxLayer() + 1 });
    state.selectedElement = { type: 'shape', index: state.shapeElements.length - 1 };
    showMessage('Element added to design', 'success');
  }

  // ============================================
  // CANVAS INTERACTION
  // ============================================

  function setupCanvasInteraction() {
    const canvas = state.canvas;
    canvas.addEventListener('mousedown',  handleMouseDown);
    canvas.addEventListener('mousemove',  handleMouseMove);
    canvas.addEventListener('mouseup',    handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove',  handleTouchMove);
    canvas.addEventListener('touchend',   handleMouseUp);
  }

  function handleMouseDown(e) {
    const rect = state.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (state.selectedElement) {
      const deletePos = getDeleteButtonPosition(state.selectedElement);
      if (deletePos && isPointInCircle(x, y, deletePos.x, deletePos.y, 12)) { deleteSelectedElement(); return; }
    }

    if (state.selectedElement) {
      const handles = getResizeHandles(state.selectedElement);
      for (let i = 0; i < handles.length; i++) {
        if (isPointInCircle(x, y, handles[i].x, handles[i].y, 8)) {
          state.isResizing = true;
          state.resizeHandle = i;
          state.dragStart = { x, y };
          if (state.selectedElement.type === 'text') {
            state.initialSize = state.textElements[state.selectedElement.index].size;
            const text = state.textElements[state.selectedElement.index];
            state.initialDistance = Math.sqrt(Math.pow(x - text.x, 2) + Math.pow(y - text.y, 2));
          } else if (state.selectedElement.type === 'shape') {
            state.initialSize = state.shapeElements[state.selectedElement.index].size;
            const shape = state.shapeElements[state.selectedElement.index];
            state.initialDistance = Math.sqrt(Math.pow(x - shape.x, 2) + Math.pow(y - shape.y, 2));
          } else if (state.selectedElement.type === 'image') {
            state.initialSize = state.imageScale;
            state.initialDistance = Math.sqrt(Math.pow(x - state.imagePosition.x, 2) + Math.pow(y - state.imagePosition.y, 2));
          }
          return;
        }
      }
    }

    for (let i = state.textElements.length - 1; i >= 0; i--) {
      const text = state.textElements[i];
      if (isPointInRect(x, y, text.x - text.width/2, text.y - text.height/2, text.width, text.height)) {
        state.selectedElement = { type: 'text', index: i };
        state.isDragging = true;
        state.dragOffset = { x: x - text.x, y: y - text.y };
        updateTextControls(text);
        return;
      }
    }

    for (let i = state.shapeElements.length - 1; i >= 0; i--) {
      const shape = state.shapeElements[i];
      if (isPointInCircle(x, y, shape.x, shape.y, shape.size/2)) {
        state.selectedElement = { type: 'shape', index: i };
        state.isDragging = true;
        state.dragOffset = { x: x - shape.x, y: y - shape.y };
        return;
      }
    }

    if (state.uploadedImage) {
      const sw = state.uploadedImage.width  * state.imageScale;
      const sh = state.uploadedImage.height * state.imageScale;
      if (isPointInRect(x, y, state.imagePosition.x - sw/2, state.imagePosition.y - sh/2, sw, sh)) {
        state.selectedElement = { type: 'image', index: 0 };
        state.isDragging = true;
        state.dragOffset = { x: x - state.imagePosition.x, y: y - state.imagePosition.y };
        return;
      }
    }

    state.selectedElement = null;
  }

  function handleMouseMove(e) {
    const rect = state.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (state.isResizing && state.selectedElement) handleResize(x, y);
    else if (state.isDragging && state.selectedElement) handleDrag(x, y);
  }

  function handleMouseUp() {
    state.isDragging  = false;
    state.isResizing  = false;
    state.resizeHandle = null;
  }

  function handleTouchStart(e) {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleMouseDown(new MouseEvent('mousedown', { clientX: touch.clientX, clientY: touch.clientY }));
      e.preventDefault();
    }
  }

  function handleTouchMove(e) {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleMouseMove(new MouseEvent('mousemove', { clientX: touch.clientX, clientY: touch.clientY }));
      e.preventDefault();
    }
  }

  function handleDrag(x, y) {
    const newX = x - state.dragOffset.x;
    const newY = y - state.dragOffset.y;
    if (state.selectedElement.type === 'text') {
      state.textElements[state.selectedElement.index].x = newX;
      state.textElements[state.selectedElement.index].y = newY;
    } else if (state.selectedElement.type === 'shape') {
      state.shapeElements[state.selectedElement.index].x = newX;
      state.shapeElements[state.selectedElement.index].y = newY;
    } else if (state.selectedElement.type === 'image') {
      state.imagePosition.x = newX;
      state.imagePosition.y = newY;
    }
  }

  function handleResize(x, y) {
    let centerX, centerY;
    if (state.selectedElement.type === 'text') {
      const text = state.textElements[state.selectedElement.index];
      centerX = text.x; centerY = text.y;
    } else if (state.selectedElement.type === 'shape') {
      const shape = state.shapeElements[state.selectedElement.index];
      centerX = shape.x; centerY = shape.y;
    } else if (state.selectedElement.type === 'image') {
      centerX = state.imagePosition.x; centerY = state.imagePosition.y;
    }
    const currentDistance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
    const ratio = currentDistance / state.initialDistance;
    if (state.selectedElement.type === 'text') {
      state.textElements[state.selectedElement.index].size = Math.max(12, state.initialSize * ratio);
    } else if (state.selectedElement.type === 'shape') {
      state.shapeElements[state.selectedElement.index].size = Math.max(20, state.initialSize * ratio);
    } else if (state.selectedElement.type === 'image') {
      state.imageScale = Math.max(0.01, state.initialSize * ratio);
      if (state.uploadedImage) {
        const baseFit = 450 / Math.max(state.uploadedImage.width, state.uploadedImage.height);
        const zp = Math.round((state.imageScale / baseFit) * 100);
        const zoomSlider = document.getElementById('zoomSlider');
        const zoomValue  = document.getElementById('zoomValue');
        if (zoomSlider) zoomSlider.value = Math.max(10, Math.min(2000, zp));
        if (zoomValue)  zoomValue.textContent = Math.max(10, Math.min(2000, zp)) + '%';
      }
    }
  }

  function setupKeyboardControls() {
    document.addEventListener('keydown', function(e) {
      const textInput  = document.getElementById('textInput');
      const isTyping   = textInput && document.activeElement === textInput;
      if ((e.key === 'Delete' || e.key === 'Backspace') && state.selectedElement && !isTyping) {
        e.preventDefault();
        deleteSelectedElement();
      }
    });
  }

  function deleteSelectedElement() {
    if (!state.selectedElement) return;
    if (state.selectedElement.type === 'text') {
      state.textElements.splice(state.selectedElement.index, 1);
      document.getElementById('textInput').value = '';
      state.currentTextInput = '';
    } else if (state.selectedElement.type === 'shape') {
      state.shapeElements.splice(state.selectedElement.index, 1);
    } else if (state.selectedElement.type === 'image') {
      state.uploadedImage = null;
      state.uploadedImageUrl = null;
      state.cloudinaryUrl = null;
      state.imageScale = 1;
      state.imageLayer = 1;
      state.imagePosition = { x: 225, y: 225 };
      state.imageRotation = 0;
      state.brightness = 100;
      state.contrast = 100;
      state.saturation = 100;
      state.flipHorizontal = false;
      state.flipVertical = false;
      document.getElementById('uploadedImageInfo').style.display = 'none';
      const uploadPrompt = document.getElementById('uploadPrompt');
      if (uploadPrompt) uploadPrompt.classList.remove('hidden');
      const fi = document.getElementById('fileInput');
      if (fi) fi.value = '';
      const zoomSlider = document.getElementById('zoomSlider');
      const zoomValue  = document.getElementById('zoomValue');
      if (zoomSlider) zoomSlider.value = 100;
      if (zoomValue)  zoomValue.textContent = '100%';
    }
    state.selectedElement = null;
    showMessage('Element deleted', 'success');
  }

  function updateTextControls(text) {
    const textInput           = document.getElementById('textInput');
    const fontSelected        = document.getElementById('fontSelected');
    const fontSizeSlider      = document.getElementById('fontSizeSlider');
    const fontSizeValue       = document.getElementById('fontSizeValue');
    const letterSpacingSlider = document.getElementById('letterSpacingSlider');
    const letterSpacingValue  = document.getElementById('letterSpacingValue');
    const lineHeightSlider    = document.getElementById('lineHeightSlider');
    const lineHeightValue     = document.getElementById('lineHeightValue');
    if (textInput)           textInput.value = text.text;
    if (fontSelected)        fontSelected.textContent = text.font;
    if (fontSizeSlider)      fontSizeSlider.value = text.size;
    if (fontSizeValue)       fontSizeValue.textContent = text.size + 'px';
    if (letterSpacingSlider) letterSpacingSlider.value = text.letterSpacing;
    if (letterSpacingValue)  letterSpacingValue.textContent = text.letterSpacing + 'px';
    if (lineHeightSlider)    lineHeightSlider.value = text.lineHeight;
    if (lineHeightValue)     lineHeightValue.textContent = text.lineHeight;
    state.currentTextInput  = text.text;
    state.selectedFont      = text.font;
    state.fontSize          = text.size;
    state.letterSpacing     = text.letterSpacing;
    state.lineHeight        = text.lineHeight;
    state.selectedTextColor = text.color;
  }

  // ============================================
  // HIT TESTING
  // ============================================

  function isPointInRect(px, py, x, y, w, h) { return px >= x && px <= x + w && py >= y && py <= y + h; }
  function isPointInCircle(px, py, cx, cy, r) { return (px-cx)*(px-cx) + (py-cy)*(py-cy) <= r*r; }

  function getResizeHandles(element) {
    if (!element) return [];
    if (element.type === 'text') {
      const t = state.textElements[element.index];
      const w = t.width, h = t.height, cx = t.x, cy = t.y;
      return [
        { x: cx-w/2, y: cy-h/2 }, { x: cx+w/2, y: cy-h/2 },
        { x: cx-w/2, y: cy+h/2 }, { x: cx+w/2, y: cy+h/2 },
        { x: cx, y: cy-h/2 },     { x: cx, y: cy+h/2 },
        { x: cx-w/2, y: cy },     { x: cx+w/2, y: cy }
      ];
    } else if (element.type === 'shape') {
      const s = state.shapeElements[element.index];
      const h = s.size/2;
      return [
        { x: s.x-h, y: s.y-h }, { x: s.x+h, y: s.y-h },
        { x: s.x-h, y: s.y+h }, { x: s.x+h, y: s.y+h },
        { x: s.x, y: s.y-h },   { x: s.x, y: s.y+h },
        { x: s.x-h, y: s.y },   { x: s.x+h, y: s.y }
      ];
    } else if (element.type === 'image' && state.uploadedImage) {
      const sw = state.uploadedImage.width  * state.imageScale;
      const sh = state.uploadedImage.height * state.imageScale;
      const cx = state.imagePosition.x, cy = state.imagePosition.y;
      return [
        { x: cx-sw/2, y: cy-sh/2 }, { x: cx+sw/2, y: cy-sh/2 },
        { x: cx-sw/2, y: cy+sh/2 }, { x: cx+sw/2, y: cy+sh/2 },
        { x: cx, y: cy-sh/2 },      { x: cx, y: cy+sh/2 },
        { x: cx-sw/2, y: cy },      { x: cx+sw/2, y: cy }
      ];
    }
    return [];
  }

  function getDeleteButtonPosition(element) {
    if (!element) return null;
    if (element.type === 'text') {
      const t = state.textElements[element.index];
      return { x: t.x + t.width/2, y: t.y - t.height/2 };
    } else if (element.type === 'shape') {
      const s = state.shapeElements[element.index];
      return { x: s.x + s.size/2, y: s.y - s.size/2 };
    } else if (element.type === 'image' && state.uploadedImage) {
      const sw = state.uploadedImage.width  * state.imageScale;
      const sh = state.uploadedImage.height * state.imageScale;
      return { x: state.imagePosition.x + sw/2, y: state.imagePosition.y - sh/2 };
    }
    return null;
  }

  // ============================================
  // RENDER — PREVIEW (with holographic bg)
  // ============================================

  function renderCanvas() {
    const ctx    = state.ctx;
    const canvas = state.canvas;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawHolographicBackground(ctx);

    const allElements = buildElementList();
    allElements.forEach(el => {
      if (el.type === 'image') drawImage(ctx);
      else if (el.type === 'text') drawText(ctx, state.textElements[el.index]);
      else if (el.type === 'shape') drawElement(ctx, state.shapeElements[el.index]);
    });

    if (state.selectedElement) drawSelectionUI(ctx, state.selectedElement);
  }

  function buildElementList() {
    const list = [];
    if (state.uploadedImage) list.push({ type: 'image', layer: state.imageLayer || 1 });
    state.textElements.forEach((t, i)  => list.push({ type: 'text',  index: i, layer: t.layer || 0 }));
    state.shapeElements.forEach((s, i) => list.push({ type: 'shape', index: i, layer: s.layer || 0 }));
    list.sort((a, b) => a.layer - b.layer);
    return list;
  }

  function drawImage(ctx) {
    if (!state.uploadedImage) return;
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(0, 0, 450, 450, 20);
    ctx.clip();
    ctx.translate(state.imagePosition.x, state.imagePosition.y);
    ctx.rotate((state.imageRotation * Math.PI) / 180);
    if (state.flipHorizontal) ctx.scale(-1, 1);
    if (state.flipVertical)   ctx.scale(1, -1);
    ctx.filter = `brightness(${state.brightness}%) contrast(${state.contrast}%) saturate(${state.saturation}%)`;
    const sw = state.uploadedImage.width  * state.imageScale;
    const sh = state.uploadedImage.height * state.imageScale;
    ctx.drawImage(state.uploadedImage, -sw/2, -sh/2, sw, sh);
    ctx.restore();
  }

  function drawText(ctx, textElement) {
    ctx.save();
    ctx.filter = 'none';
    ctx.globalCompositeOperation = 'source-over';
    ctx.font = `${textElement.size}px '${textElement.font}', sans-serif`;
    ctx.fillStyle = textElement.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const lines = textElement.text.split('\n');
    const lineHeightPx = textElement.size * textElement.lineHeight;
    const totalHeight  = lines.length * lineHeightPx;
    const startY = textElement.y - totalHeight/2 + lineHeightPx/2;
    lines.forEach((line, index) => {
      const currentY = startY + index * lineHeightPx;
      if (textElement.letterSpacing !== 0) {
        const letters = line.split('');
        let currentX = textElement.x - ctx.measureText(line).width/2;
        letters.forEach(letter => {
          ctx.fillText(letter, currentX, currentY);
          currentX += ctx.measureText(letter).width + textElement.letterSpacing;
        });
      } else {
        if (textElement.outline) { ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 3; ctx.strokeText(line, textElement.x, currentY); }
        if (textElement.gradient) {
          const grad = ctx.createLinearGradient(textElement.x-100, currentY, textElement.x+100, currentY);
          grad.addColorStop(0, textElement.color);
          grad.addColorStop(1, '#FFFFFF');
          ctx.fillStyle = grad;
        }
        ctx.fillText(line, textElement.x, currentY);
      }
    });
    const maxLineWidth = Math.max(...lines.map(l => ctx.measureText(l).width));
    textElement.width  = maxLineWidth + 20;
    textElement.height = totalHeight + 20;
    ctx.restore();
  }

  function drawElement(ctx, element) {
    ctx.save();
    ctx.translate(element.x, element.y);
    ctx.rotate((element.rotation * Math.PI) / 180);
    ctx.fillStyle = element.color;
    const s = element.size;
    switch(element.type) {
      case 'star': case 'star-filled': drawStar(ctx, 0, 0, 5, s, s/2); break;
      case 'heart':    drawHeart(ctx, 0, 0, s); break;
      case 'circle':   ctx.beginPath(); ctx.arc(0, 0, s/2, 0, Math.PI*2); ctx.fill(); break;
      case 'square':   ctx.fillRect(-s/2, -s/2, s, s); break;
      case 'triangle': drawTriangle(ctx, 0, 0, s); break;
      case 'arrow':    drawArrow(ctx, 0, 0, s); break;
      case 'diamond':  drawDiamond(ctx, 0, 0, s); break;
      case 'flag':     drawFlag(ctx, 0, 0, s); break;
    }
    ctx.restore();
  }

  function drawStar(ctx, cx, cy, spikes, outer, inner) {
    let rot = Math.PI/2*3, step = Math.PI/spikes;
    ctx.beginPath(); ctx.moveTo(cx, cy - outer);
    for (let i = 0; i < spikes; i++) {
      ctx.lineTo(cx + Math.cos(rot)*outer, cy + Math.sin(rot)*outer); rot += step;
      ctx.lineTo(cx + Math.cos(rot)*inner, cy + Math.sin(rot)*inner); rot += step;
    }
    ctx.lineTo(cx, cy - outer); ctx.closePath(); ctx.fill();
  }
  function drawHeart(ctx, x, y, s) {
    const h = s*0.3;
    ctx.beginPath(); ctx.moveTo(x, y+h);
    ctx.bezierCurveTo(x, y, x-s/2, y, x-s/2, y+h);
    ctx.bezierCurveTo(x-s/2, y+(s+h)/2, x, y+(s+h)/2, x, y+s);
    ctx.bezierCurveTo(x, y+(s+h)/2, x+s/2, y+(s+h)/2, x+s/2, y+h);
    ctx.bezierCurveTo(x+s/2, y, x, y, x, y+h);
    ctx.closePath(); ctx.fill();
  }
  function drawTriangle(ctx, x, y, s) { ctx.beginPath(); ctx.moveTo(x, y-s/2); ctx.lineTo(x+s/2, y+s/2); ctx.lineTo(x-s/2, y+s/2); ctx.closePath(); ctx.fill(); }
  function drawArrow(ctx, x, y, s) { ctx.beginPath(); ctx.moveTo(x, y-s/2); ctx.lineTo(x+s/2, y); ctx.lineTo(x+s/4, y); ctx.lineTo(x+s/4, y+s/2); ctx.lineTo(x-s/4, y+s/2); ctx.lineTo(x-s/4, y); ctx.lineTo(x-s/2, y); ctx.closePath(); ctx.fill(); }
  function drawDiamond(ctx, x, y, s) { ctx.beginPath(); ctx.moveTo(x, y-s/2); ctx.lineTo(x+s/2, y); ctx.lineTo(x, y+s/2); ctx.lineTo(x-s/2, y); ctx.closePath(); ctx.fill(); }
  function drawFlag(ctx, x, y, s) { ctx.fillRect(x-s/2, y-s/2, s*0.15, s); ctx.beginPath(); ctx.moveTo(x-s/2+s*0.15, y-s/2); ctx.lineTo(x+s/2, y-s/4); ctx.lineTo(x-s/2+s*0.15, y); ctx.closePath(); ctx.fill(); }

  function drawSelectionUI(ctx, element) {
    ctx.save();
    ctx.strokeStyle = '#000DCA';
    ctx.lineWidth = 2;
    if (element.type === 'text') {
      const t = state.textElements[element.index];
      ctx.strokeRect(t.x-t.width/2, t.y-t.height/2, t.width, t.height);
    } else if (element.type === 'shape') {
      const s = state.shapeElements[element.index];
      ctx.strokeRect(s.x-s.size/2, s.y-s.size/2, s.size, s.size);
    } else if (element.type === 'image' && state.uploadedImage) {
      const sw = state.uploadedImage.width  * state.imageScale;
      const sh = state.uploadedImage.height * state.imageScale;
      ctx.strokeRect(state.imagePosition.x-sw/2, state.imagePosition.y-sh/2, sw, sh);
    }
    getResizeHandles(element).forEach(h => {
      ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.arc(h.x, h.y, 8, 0, Math.PI*2); ctx.fill();
      ctx.strokeStyle = '#000DCA'; ctx.stroke();
    });
    const del = getDeleteButtonPosition(element);
    if (del) {
      ctx.fillStyle = '#CF0000'; ctx.beginPath(); ctx.arc(del.x, del.y, 12, 0, Math.PI*2); ctx.fill();
      ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(del.x-5, del.y-5); ctx.lineTo(del.x+5, del.y+5); ctx.moveTo(del.x+5, del.y-5); ctx.lineTo(del.x-5, del.y+5); ctx.stroke();
    }
    ctx.restore();
  }

  // ============================================
  // HIGH-RES EXPORT — white square background
  // ============================================

  function renderHighResCanvas(upscaledImage) {
    const ctx   = state.exportCtx;
    const scale = 4;
    ctx.clearRect(0, 0, 1800, 1800);
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.roundRect(0, 0, 1800, 1800, 80);
    ctx.fill();

    const allElements = buildElementList();
    allElements.forEach(el => {
      if (el.type === 'image') drawImageHighRes(ctx, scale, upscaledImage);
      else if (el.type === 'text') drawTextHighRes(ctx, state.textElements[el.index], scale);
      else if (el.type === 'shape') drawElementHighRes(ctx, state.shapeElements[el.index], scale);
    });
  }

  function drawImageHighRes(ctx, scale, upscaledImage) {
    const imgToDraw = upscaledImage || state.uploadedImage;
    if (!imgToDraw) return;
    let adjustedScale = state.imageScale;
    if (upscaledImage && state.uploadedImage) {
      adjustedScale = state.imageScale * (state.uploadedImage.width / upscaledImage.width);
    }
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(0, 0, 1800, 1800, 80);
    ctx.clip();
    ctx.translate(state.imagePosition.x * scale, state.imagePosition.y * scale);
    ctx.rotate((state.imageRotation * Math.PI) / 180);
    if (state.flipHorizontal) ctx.scale(-1, 1);
    if (state.flipVertical)   ctx.scale(1, -1);
    ctx.filter = `brightness(${state.brightness}%) contrast(${state.contrast}%) saturate(${state.saturation}%)`;
    const sw = imgToDraw.width  * adjustedScale * scale;
    const sh = imgToDraw.height * adjustedScale * scale;
    ctx.drawImage(imgToDraw, -sw/2, -sh/2, sw, sh);
    ctx.restore();
  }

  function drawTextHighRes(ctx, textElement, scale) {
    ctx.save();
    const scaledSize = textElement.size * scale;
    ctx.font = `${scaledSize}px '${textElement.font}', sans-serif`;
    ctx.fillStyle = textElement.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const lines = textElement.text.split('\n');
    const lineHeightPx = scaledSize * textElement.lineHeight;
    const totalHeight  = lines.length * lineHeightPx;
    const startY = (textElement.y * scale) - totalHeight/2 + lineHeightPx/2;
    lines.forEach((line, index) => {
      const currentY = startY + index * lineHeightPx;
      if (textElement.letterSpacing !== 0) {
        const letters = line.split('');
        let currentX = (textElement.x * scale) - ctx.measureText(line).width/2;
        letters.forEach(letter => {
          ctx.fillText(letter, currentX, currentY);
          currentX += ctx.measureText(letter).width + textElement.letterSpacing * scale;
        });
      } else {
        if (textElement.outline) { ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 3*scale; ctx.strokeText(line, textElement.x*scale, currentY); }
        if (textElement.gradient) {
          const grad = ctx.createLinearGradient((textElement.x-100)*scale, currentY, (textElement.x+100)*scale, currentY);
          grad.addColorStop(0, textElement.color); grad.addColorStop(1, '#FFFFFF');
          ctx.fillStyle = grad;
        }
        ctx.fillText(line, textElement.x * scale, currentY);
      }
    });
    ctx.restore();
  }

  function drawElementHighRes(ctx, element, scale) {
    ctx.save();
    ctx.translate(element.x*scale, element.y*scale);
    ctx.rotate((element.rotation * Math.PI) / 180);
    ctx.fillStyle = element.color;
    const s = element.size * scale;
    switch(element.type) {
      case 'star': case 'star-filled': drawStar(ctx, 0, 0, 5, s, s/2); break;
      case 'heart':    drawHeart(ctx, 0, 0, s); break;
      case 'circle':   ctx.beginPath(); ctx.arc(0, 0, s/2, 0, Math.PI*2); ctx.fill(); break;
      case 'square':   ctx.fillRect(-s/2, -s/2, s, s); break;
      case 'triangle': drawTriangle(ctx, 0, 0, s); break;
      case 'arrow':    drawArrow(ctx, 0, 0, s); break;
      case 'diamond':  drawDiamond(ctx, 0, 0, s); break;
      case 'flag':     drawFlag(ctx, 0, 0, s); break;
    }
    ctx.restore();
  }

  // ============================================
  // SAVE BUTTON
  // ============================================

  function setupSaveButton() {
    const saveBtn = document.getElementById('saveDesignBtn');
    if (!saveBtn) return;
    saveBtn.addEventListener('click', function() {
      if (!state.uploadedImage && state.textElements.length === 0 && state.shapeElements.length === 0) {
        showMessage('Please add content to your design before saving', 'error');
        return;
      }
      state.selectedElement = null;
      showMessage('Generating high-resolution file...', 'info');

      if (state.uploadedImage && state.cloudinaryUrl) {
        const upscaledUrl = state.cloudinaryUrl.replace('/upload/', '/upload/e_upscale,q_auto:best,f_png/');
        const upscaledImg = new Image();
        upscaledImg.crossOrigin = 'anonymous';
        upscaledImg.onload = function() {
          renderHighResCanvas(upscaledImg);
          state.exportCanvas.toBlob(blob => {
            if (!blob) { showMessage('Error generating design file', 'error'); return; }
            uploadHighResToCloudinary(blob);
          }, 'image/png', 1.0);
        };
        upscaledImg.onerror = function() {
          showMessage('Using original image quality', 'info');
          renderHighResCanvas();
          state.exportCanvas.toBlob(blob => {
            if (!blob) { showMessage('Error generating design file', 'error'); return; }
            uploadHighResToCloudinary(blob);
          }, 'image/png', 1.0);
        };
        upscaledImg.src = upscaledUrl;
      } else {
        renderHighResCanvas();
        state.exportCanvas.toBlob(blob => {
          if (!blob) { showMessage('Error generating design file', 'error'); return; }
          uploadHighResToCloudinary(blob);
        }, 'image/png', 1.0);
      }
    });
  }

  function uploadHighResToCloudinary(blob) {
    showMessage('Uploading print-ready file...', 'info');
    const formData = new FormData();
    formData.append('file', blob, 'holographic-square-design-highres.png');
    formData.append('upload_preset', window.cloudinaryConfig.uploadPreset);
    formData.append('folder', window.cloudinaryConfig.folder + '/print-ready');
    fetch(`https://api.cloudinary.com/v1_1/${window.cloudinaryConfig.cloudName}/image/upload`, {
      method: 'POST', body: formData
    })
    .then(r => r.json())
    .then(data => {
      if (data.secure_url) uploadPreviewToCloudinary(data.secure_url);
      else showMessage('Upload failed. Please try again.', 'error');
    })
    .catch(() => showMessage('Upload failed. Please try again.', 'error'));
  }

  function uploadPreviewToCloudinary(highResUrl) {
    state.canvas.toBlob(function(previewBlob) {
      const formData = new FormData();
      formData.append('file', previewBlob, 'holographic-square-design-preview.jpg');
      formData.append('upload_preset', window.cloudinaryConfig.uploadPreset);
      formData.append('folder', window.cloudinaryConfig.folder + '/previews');
      fetch(`https://api.cloudinary.com/v1_1/${window.cloudinaryConfig.cloudName}/image/upload`, {
        method: 'POST', body: formData
      })
      .then(r => r.json())
      .then(data => {
        if (data.secure_url) addToCartWithDesign(highResUrl, data.secure_url);
        else showMessage('Upload failed. Please try again.', 'error');
      })
      .catch(() => showMessage('Upload failed. Please try again.', 'error'));
    }, 'image/jpeg', 0.85);
  }

  function addToCartWithDesign(highResUrl, previewUrl) {
    if (!state.productData.variantId) tryToFindVariant();
    if (!state.productData.variantId) findVariantAndAddToCart(highResUrl, previewUrl);
    else addToCartWithVariant(state.productData.variantId, highResUrl, previewUrl);
  }

  function findVariantAndAddToCart(highResUrl, previewUrl) {
    showMessage('Finding product variant...', 'info');
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return_url') || document.referrer;
    let productHandle = 'square-holographic-stickers';
    if (returnUrl) {
      const match = returnUrl.match(/\/products\/([^?\/]+)/);
      if (match && match[1]) productHandle = match[1];
    }
    fetch(`/products/${productHandle}.js`)
    .then(r => r.json())
    .then(product => {
      if (product.variants && product.variants.length > 0) {
        state.productData.variantId = product.variants[0].id;
        addToCartWithVariant(state.productData.variantId, highResUrl, previewUrl);
      } else {
        showMessage('Could not find product variant. Please contact support.', 'error');
      }
    })
    .catch(() => showMessage('Error finding product. Please go back and try again.', 'error'));
  }

  function addToCartWithVariant(variantId, highResUrl, previewUrl) {
    const formData = {
      items: [{
        id: variantId,
        quantity: 1,
        properties: {
          'Custom Design': 'Yes',
          'Sticker Type': 'Holographic Square',
          '_Design Preview': previewUrl,
          '_Print File': highResUrl,
          '_Design Specs': '1800x1800px PNG',
          'Product Details': (state.productData.quantity || 'Custom') + ' • ' + (state.productData.size || 'Custom size'),
          '_Has Text': state.textElements.length > 0 ? 'Yes' : 'No',
          '_Has Elements': state.shapeElements.length > 0 ? 'Yes' : 'No'
        }
      }]
    };
    showMessage('Adding design to cart...', 'info');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
    .then(r => { if (!r.ok) throw new Error('Cart add failed'); return r.json(); })
    .then(() => {
      showMessage('Added to cart! Redirecting...', 'success');
      setTimeout(() => { window.location.href = '/cart'; }, 800);
    })
    .catch(err => showMessage('Error adding to cart: ' + err.message, 'error'));
  }

  // ============================================
  // RESET
  // ============================================

  function setupResetButton() {
    const resetBtn = document.getElementById('resetAllBtn');
    if (!resetBtn) return;
    resetBtn.addEventListener('click', function() {
      if (!confirm('Are you sure you want to reset your design? This cannot be undone.')) return;
      state.uploadedImage = null;
      state.uploadedImageUrl = null;
      state.cloudinaryUrl = null;
      state.imagePosition = { x: 225, y: 225 };
      state.imageScale = 1;
      state.imageRotation = 0;
      state.brightness = 100;
      state.contrast = 100;
      state.saturation = 100;
      state.flipHorizontal = false;
      state.flipVertical = false;
      state.textElements = [];
      state.shapeElements = [];
      state.selectedElement = null;
      state.currentTextInput = '';
      state.imageLayer = 1;
      document.getElementById('uploadedImageInfo').style.display = 'none';
      const uploadPrompt = document.getElementById('uploadPrompt');
      if (uploadPrompt) uploadPrompt.classList.remove('hidden');
      const fi = document.getElementById('fileInput');
      if (fi) fi.value = '';
      document.getElementById('textInput').value = '';
      document.getElementById('zoomSlider').value = 100;
      document.getElementById('zoomValue').textContent = '100%';
      document.getElementById('rotationSlider').value = 0;
      document.getElementById('rotationValue').textContent = '0°';
      document.getElementById('brightnessSlider').value = 100;
      document.getElementById('brightnessValue').textContent = '100%';
      document.getElementById('contrastSlider').value = 100;
      document.getElementById('contrastValue').textContent = '100%';
      document.getElementById('saturationSlider').value = 100;
      document.getElementById('saturationValue').textContent = '100%';
      showMessage('Design reset successfully', 'success');
    });
  }

  // ============================================
  // MESSAGES
  // ============================================

  function showMessage(message, type) {
    const existing = document.querySelector('.message-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = `message-toast ${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => { toast.style.animation = 'slideOut 0.3s ease'; setTimeout(() => toast.remove(), 300); }, 3000);
  }

  window.customizerState  = state;
  window.customizerRender = renderCanvas;

})();


// ============================================
// MOBILE — Toolbar and Panel Controls
// ============================================

(function() {
  'use strict';
  if (window.innerWidth > 768) return;

  const toolbarIcons = document.querySelectorAll('.mobile-toolbar-icon');
  const panels       = document.querySelectorAll('.mobile-slide-panel');
  const overlay      = document.querySelector('.mobile-panel-overlay');
  const closeButtons = document.querySelectorAll('.mobile-panel-close');
  let startY = 0, currentY = 0, isDragging = false, activePanel = null;

  toolbarIcons.forEach(icon => {
    icon.addEventListener('click', function() {
      const panelId = this.getAttribute('data-panel');
      const panel   = document.getElementById(panelId);
      panels.forEach(p => p.classList.remove('active'));
      toolbarIcons.forEach(i => i.classList.remove('active'));
      if (panel) {
        panel.classList.add('active');
        this.classList.add('active');
        overlay.classList.add('active');
        activePanel = panel;
        if (panelId === 'mobileTextPanel') {
          setTimeout(function() {
            const state = window.customizerState;
            if (state && state.textElements.length > 0) {
              state.selectedElement = { type: 'text', index: 0 };
              const mobileTextInput = document.getElementById('mobileTextInput');
              if (mobileTextInput) mobileTextInput.value = state.textElements[0].text;
            }
          }, 200);
        }
      }
    });
  });

  closeButtons.forEach(btn => btn.addEventListener('click', closeAllPanels));
  if (overlay) overlay.addEventListener('click', closeAllPanels);

  panels.forEach(panel => {
    const panelHeader = panel.querySelector('.mobile-panel-header');
    panelHeader.addEventListener('touchstart', function(e) { startY = e.touches[0].clientY; isDragging = true; panel.style.transition = 'none'; });
    panelHeader.addEventListener('touchmove', function(e) {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      const delta = currentY - startY;
      if (delta > 0) panel.style.transform = `translateY(${delta}px)`;
    });
    panelHeader.addEventListener('touchend', function() {
      if (!isDragging) return;
      isDragging = false;
      panel.style.transition = 'transform 0.3s ease';
      if (currentY - startY > 100) closeAllPanels();
      else panel.style.transform = 'translateY(0)';
    });
  });

  function closeAllPanels() {
    panels.forEach(p => { p.style.transition = 'transform 0.3s ease'; p.style.transform = ''; p.classList.remove('active'); });
    toolbarIcons.forEach(i => i.classList.remove('active'));
    overlay.classList.remove('active');
    activePanel = null;
  }
})();


// ============================================
// MOBILE — Touch Interactions and Real-Time Updates
// ============================================

(function() {
  'use strict';
  if (window.innerWidth > 768) return;

  setTimeout(function() {
    const canvas = document.getElementById('designCanvas');
    if (!canvas) return;

    const mobileUploadArea = document.getElementById('mobileUploadArea');
    const mobileFileInput  = document.getElementById('mobileFileInput');
    if (mobileUploadArea && mobileFileInput) {
      mobileUploadArea.addEventListener('click', function() { mobileFileInput.click(); });
      mobileFileInput.addEventListener('change', function(e) {
        if (e.target.files && e.target.files[0]) {
          const desktopFileInput = document.getElementById('fileInput');
          if (desktopFileInput) {
            const dt = new DataTransfer();
            dt.items.add(e.target.files[0]);
            desktopFileInput.files = dt.files;
            desktopFileInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    const mobileTextInput  = document.getElementById('mobileTextInput');
    const desktopTextInput = document.getElementById('textInput');
    if (mobileTextInput && desktopTextInput) {
      mobileTextInput.addEventListener('input', function() {
        const state = window.customizerState;
        if (state && state.textElements.length > 0 && (!state.selectedElement || state.selectedElement.type !== 'text')) {
          state.selectedElement = { type: 'text', index: 0 };
        }
        desktopTextInput.value = this.value;
        desktopTextInput.dispatchEvent(new Event('input', { bubbles: true }));
      });
      setInterval(function() {
        if (desktopTextInput.value !== mobileTextInput.value) mobileTextInput.value = desktopTextInput.value;
      }, 500);
    }

    const mobileTextColorGrid = document.getElementById('mobileTextColorGrid');
    if (mobileTextColorGrid) {
      mobileTextColorGrid.querySelectorAll('.color-swatch').forEach(swatch => {
        swatch.addEventListener('click', function(e) {
          e.preventDefault(); e.stopPropagation();
          const color = this.getAttribute('data-color');
          const state = window.customizerState;
          if (state && !state.selectedElement && state.textElements.length > 0) {
            state.selectedElement = { type: 'text', index: 0 };
          }
          setTimeout(function() {
            const desktopGrid = document.getElementById('textColorGrid');
            if (desktopGrid && color) {
              const ds = desktopGrid.querySelector(`[data-color="${color}"]`);
              if (ds) ds.click();
            }
          }, 50);
          mobileTextColorGrid.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('selected'));
          this.classList.add('selected');
        });
      });
    }

    function setupMobileSlider(mobileId, desktopId, valueId) {
      const ms = document.getElementById(mobileId);
      const ds = document.getElementById(desktopId);
      const vd = document.getElementById(valueId);
      if (ms && ds) {
        ms.addEventListener('input', function() {
          ds.value = this.value;
          ds.dispatchEvent(new Event('input', { bubbles: true }));
          ds.dispatchEvent(new Event('change', { bubbles: true }));
          if (vd) {
            setTimeout(function() {
              const desktopValueId = valueId.replace('mobile', '').charAt(0).toLowerCase() + valueId.replace('mobile', '').slice(1);
              const dv = document.getElementById(desktopValueId);
              if (dv) vd.textContent = dv.textContent;
            }, 50);
          }
        });
      }
    }

    setupMobileSlider('mobileZoomSlider',          'zoomSlider',          'mobileZoomValue');
    setupMobileSlider('mobileRotationSlider',       'rotationSlider',      'mobileRotationValue');
    setupMobileSlider('mobileBrightnessSlider',     'brightnessSlider',    'mobileBrightnessValue');
    setupMobileSlider('mobileContrastSlider',       'contrastSlider',      'mobileContrastValue');
    setupMobileSlider('mobileSaturationSlider',     'saturationSlider',    'mobileSaturationValue');
    setupMobileSlider('mobileFontSizeSlider',       'fontSizeSlider',      'mobileFontSizeValue');
    setupMobileSlider('mobileLetterSpacingSlider',  'letterSpacingSlider', 'mobileLetterSpacingValue');
    setupMobileSlider('mobileLineHeightSlider',     'lineHeightSlider',    'mobileLineHeightValue');

    const mobileFontSelected = document.getElementById('mobileFontSelected');
    const mobileFontOptions  = document.getElementById('mobileFontOptions');
    if (mobileFontSelected && mobileFontOptions) {
      mobileFontSelected.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        mobileFontOptions.classList.toggle('open');
        mobileFontSelected.classList.toggle('open');
      });
      mobileFontOptions.querySelectorAll('.font-option').forEach(option => {
        option.addEventListener('click', function(e) {
          e.preventDefault(); e.stopPropagation();
          const fontName = this.getAttribute('data-font');
          const state = window.customizerState;
          if (state && !state.selectedElement && state.textElements.length > 0) {
            state.selectedElement = { type: 'text', index: 0 };
          }
          setTimeout(function() {
            const desktopOptions = document.getElementById('fontOptions');
            if (desktopOptions) {
              const dOpt = desktopOptions.querySelector(`[data-font="${fontName}"]`);
              if (dOpt) dOpt.click();
            }
          }, 50);
          mobileFontSelected.textContent = fontName;
          mobileFontSelected.style.fontFamily = this.style.fontFamily;
          mobileFontOptions.querySelectorAll('.font-option').forEach(o => o.classList.remove('selected'));
          this.classList.add('selected');
          mobileFontOptions.classList.remove('open');
          mobileFontSelected.classList.remove('open');
        });
      });
      document.addEventListener('click', function(e) {
        if (!e.target.closest('.font-dropdown-custom')) {
          mobileFontOptions.classList.remove('open');
          mobileFontSelected.classList.remove('open');
        }
      });
    }

    function setupMobileButton(mobileId, desktopId) {
      const mb = document.getElementById(mobileId);
      const db = document.getElementById(desktopId);
      if (mb && db) mb.addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation(); db.click(); });
    }

    setupMobileButton('mobileFlipHorizontalBtn', 'flipHorizontalBtn');
    setupMobileButton('mobileFlipVerticalBtn',   'flipVerticalBtn');
    setupMobileButton('mobileImageFrontBtn',     'imageFrontBtn');
    setupMobileButton('mobileImageBackBtn',      'imageBackBtn');
    setupMobileButton('mobileTextOutlineBtn',    'textOutlineBtn');
    setupMobileButton('mobileTextGradientBtn',   'textGradientBtn');
    setupMobileButton('mobileTextFrontBtn',      'textFrontBtn');
    setupMobileButton('mobileTextBackBtn',       'textBackBtn');
    setupMobileButton('mobileBringToFrontBtn',   'bringToFrontBtn');
    setupMobileButton('mobileSendToBackBtn',     'sendToBackBtn');
    setupMobileButton('mobileClearElementsBtn',  'clearElementsBtn');

  }, 1000);
})();


// ============================================
// MOBILE — Pinch-to-Resize and Tap-to-Select
// ============================================

(function() {
  'use strict';
  if (window.innerWidth > 768) return;

  setTimeout(function() {
    const canvas = document.getElementById('designCanvas');
    if (!canvas) return;

    let pinchState = { active: false, startDistance: 0, initialSize: 0, selectedElement: null };
    let firstTouchPos = null, firstTouchTime = 0, isDraggingMobile = false, dragOffset = { x: 0, y: 0 };

    function getTouchPos(touch) {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (touch.clientX - rect.left) * (canvas.width  / rect.width),
        y: (touch.clientY - rect.top)  * (canvas.height / rect.height)
      };
    }

    function getDistance(t1, t2) {
      const p1 = getTouchPos(t1), p2 = getTouchPos(t2);
      return Math.sqrt(Math.pow(p2.x-p1.x, 2) + Math.pow(p2.y-p1.y, 2));
    }

    function findElementAtPosition(x, y) {
      const state = window.customizerState;
      if (!state) return null;
      const found = [];
      for (let i = 0; i < state.textElements.length; i++) {
        const t = state.textElements[i];
        if (x >= t.x-t.width/2 && x <= t.x+t.width/2 && y >= t.y-t.height/2 && y <= t.y+t.height/2) found.push({ type: 'text', index: i, layer: t.layer || 0 });
      }
      for (let i = 0; i < state.shapeElements.length; i++) {
        const s = state.shapeElements[i];
        if (Math.sqrt(Math.pow(x-s.x, 2)+Math.pow(y-s.y, 2)) <= s.size/2) found.push({ type: 'shape', index: i, layer: s.layer || 0 });
      }
      if (state.uploadedImage) {
        const sw = state.uploadedImage.width*state.imageScale, sh = state.uploadedImage.height*state.imageScale;
        if (x >= state.imagePosition.x-sw/2 && x <= state.imagePosition.x+sw/2 && y >= state.imagePosition.y-sh/2 && y <= state.imagePosition.y+sh/2) found.push({ type: 'image', index: 0, layer: state.imageLayer || 1 });
      }
      if (found.length === 0) return null;
      found.sort((a, b) => b.layer - a.layer);
      return found[0];
    }

    canvas.addEventListener('touchstart', function(e) {
      const state = window.customizerState;
      if (!state) return;
      if (e.touches.length === 1) {
        firstTouchPos  = getTouchPos(e.touches[0]);
        firstTouchTime = Date.now();
        isDraggingMobile = false;
        if (state.selectedElement) {
          if (state.selectedElement.type === 'text') { const t = state.textElements[state.selectedElement.index]; dragOffset.x = firstTouchPos.x-t.x; dragOffset.y = firstTouchPos.y-t.y; }
          else if (state.selectedElement.type === 'shape') { const s = state.shapeElements[state.selectedElement.index]; dragOffset.x = firstTouchPos.x-s.x; dragOffset.y = firstTouchPos.y-s.y; }
          else if (state.selectedElement.type === 'image') { dragOffset.x = firstTouchPos.x-state.imagePosition.x; dragOffset.y = firstTouchPos.y-state.imagePosition.y; }
          e.preventDefault(); e.stopPropagation();
        }
        return;
      }
      if (e.touches.length === 2) {
        e.preventDefault(); e.stopPropagation();
        firstTouchPos = null; firstTouchTime = 0; isDraggingMobile = false;
        if (!state.selectedElement) return;
        pinchState.active = true;
        pinchState.startDistance = getDistance(e.touches[0], e.touches[1]);
        pinchState.selectedElement = state.selectedElement;
        if (state.selectedElement.type === 'text')  pinchState.initialSize = state.textElements[state.selectedElement.index].size;
        else if (state.selectedElement.type === 'shape') pinchState.initialSize = state.shapeElements[state.selectedElement.index].size;
        else if (state.selectedElement.type === 'image') pinchState.initialSize = state.imageScale;
      }
    }, true);

    canvas.addEventListener('touchmove', function(e) {
      const state = window.customizerState;
      if (!state) return;
      if (e.touches.length === 2 && pinchState.active) {
        e.preventDefault(); e.stopPropagation();
        const ratio = getDistance(e.touches[0], e.touches[1]) / pinchState.startDistance;
        const el = pinchState.selectedElement;
        if (el.type === 'text')  state.textElements[el.index].size  = Math.max(12,  Math.min(200, pinchState.initialSize * ratio));
        else if (el.type === 'shape') state.shapeElements[el.index].size = Math.max(20, Math.min(300, pinchState.initialSize * ratio));
        else if (el.type === 'image') state.imageScale = Math.max(0.1, Math.min(5, pinchState.initialSize * ratio));
        return;
      }
      if (e.touches.length === 1 && firstTouchPos && state.selectedElement) {
        const currentPos = getTouchPos(e.touches[0]);
        const dist = Math.sqrt(Math.pow(currentPos.x-firstTouchPos.x, 2)+Math.pow(currentPos.y-firstTouchPos.y, 2));
        if (dist > 10) {
          e.preventDefault(); e.stopPropagation();
          isDraggingMobile = true;
          const newX = currentPos.x - dragOffset.x, newY = currentPos.y - dragOffset.y;
          if (state.selectedElement.type === 'text') { state.textElements[state.selectedElement.index].x = newX; state.textElements[state.selectedElement.index].y = newY; }
          else if (state.selectedElement.type === 'shape') { state.shapeElements[state.selectedElement.index].x = newX; state.shapeElements[state.selectedElement.index].y = newY; }
          else if (state.selectedElement.type === 'image') { state.imagePosition.x = newX; state.imagePosition.y = newY; }
        }
      }
    }, true);

    canvas.addEventListener('touchend', function(e) {
      const state = window.customizerState;
      if (!state) return;
      if (pinchState.active) { e.preventDefault(); e.stopPropagation(); pinchState.active = false; }
      if (e.touches.length === 0 && firstTouchPos !== null && !isDraggingMobile) {
        const tapDuration = Date.now() - firstTouchTime;
        if (tapDuration < 300) {
          const element = findElementAtPosition(firstTouchPos.x, firstTouchPos.y);
          if (element) {
            e.preventDefault(); e.stopPropagation();
            state.selectedElement = element;
            if (element.type === 'text') {
              const mobileTextInput = document.getElementById('mobileTextInput');
              if (mobileTextInput) mobileTextInput.value = state.textElements[element.index].text;
            }
          } else {
            state.selectedElement = null;
          }
        }
      }
      firstTouchPos = null; firstTouchTime = 0; isDraggingMobile = false;
    }, true);

  }, 1500);

  const mobileTextInput = document.getElementById('mobileTextInput');
  if (mobileTextInput) {
    mobileTextInput.addEventListener('keydown', function(e) {
      if (e.key === 'Delete' || e.key === 'Backspace') e.stopPropagation();
    }, true);
  }

  const deleteBtn = document.getElementById('mobileFloatingDelete');
  if (deleteBtn) {
    setInterval(function() {
      const state = window.customizerState;
      deleteBtn.style.display = (state && state.selectedElement) ? 'block' : 'none';
    }, 200);
    deleteBtn.addEventListener('click', function() {
      const state = window.customizerState;
      if (!state || !state.selectedElement) return;
      if (state.selectedElement.type === 'text') {
        state.textElements.splice(state.selectedElement.index, 1);
        const mobileTextInput = document.getElementById('mobileTextInput');
        if (mobileTextInput) mobileTextInput.value = '';
      } else if (state.selectedElement.type === 'shape') {
        state.shapeElements.splice(state.selectedElement.index, 1);
      } else if (state.selectedElement.type === 'image') {
        const db = document.getElementById('removeImageBtn');
        if (db) db.click();
      }
      state.selectedElement = null;
    });
  }
})();